import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  UserPlus,
  Shield,
  Lock,
  Mail,
  Eye,
  EyeOff,
  KeyRound,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Add Account Modal (iOS-Style)
 * Allows adding multiple accounts to the device and switching seamlessly like Instagram.
 */
export function AddAccountModal({ isOpen, onClose, onAccountAdded }) {
  const { addAccount, verify2FAAddAccount } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 2FA step state
  const [is2FAStep, setIs2FAStep] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingPassword, setPendingPassword] = useState('');

  // Touch drag state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartY = useRef(0);
  const currentDragY = useRef(0);

  const otpInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setPassword('');
      setShowPassword(false);
      setError('');
      setIs2FAStep(false);
      setTempToken('');
      setOtpCode('');
      setPendingEmail('');
      setPendingPassword('');
      setDragY(0);
      setIsDragging(false);
      currentDragY.current = 0;
    }
  }, [isOpen]);

  const handleTouchStart = (e) => {
    const touch = e.touches ? e.touches[0] : e;
    const isHandle = e.target.closest('.ios-sheet-handle-bar') || e.target.closest('.ios-modal-header');
    if (isHandle) {
      touchStartY.current = touch.clientY;
      setIsDragging(true);
      currentDragY.current = 0;
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches ? e.touches[0] : e;
    const deltaY = touch.clientY - touchStartY.current;
    if (deltaY > 0) {
      currentDragY.current = deltaY;
      setDragY(deltaY);
    } else {
      currentDragY.current = 0;
      setDragY(0);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (currentDragY.current > 75) {
      onClose();
    }
    setDragY(0);
    currentDragY.current = 0;
  };

  useEffect(() => {
    if (is2FAStep && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [is2FAStep]);

  if (!isOpen) return null;

  const handleSubmitCredentials = async (e) => {
    e.preventDefault();
    setError('');

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError('Please enter your account email.');
      return;
    }
    if (!password) {
      setError('Please enter your master password.');
      return;
    }

    setLoading(true);
    try {
      const result = await addAccount(cleanEmail, password);
      if (result?.requires2FA) {
        setIs2FAStep(true);
        setTempToken(result.tempToken);
        setPendingEmail(result.email);
        setPendingPassword(result.masterPassword);
        setLoading(false);
        return;
      }

      setLoading(false);
      if (onAccountAdded) onAccountAdded(result.user);
      onClose();
    } catch (err) {
      setLoading(false);
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to authenticate account. Please verify credentials.';
      setError(msg);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');

    const cleanCode = otpCode.trim();
    if (cleanCode.length !== 6) {
      setError('Please enter the full 6-digit authentication code.');
      return;
    }

    setLoading(true);
    try {
      const result = await verify2FAAddAccount(
        tempToken,
        cleanCode,
        pendingEmail,
        pendingPassword
      );
      setLoading(false);
      if (onAccountAdded) onAccountAdded(result.user);
      onClose();
    } catch (err) {
      setLoading(false);
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Invalid 2FA code. Please try again.';
      setError(msg);
    }
  };

  const modalContent = (
    <div className="ios-modal-backdrop animate-fade-in" onClick={onClose} role="presentation">
      <div
        className="ios-modal-container ios-add-account-modal animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: isDragging ? 'none' : 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          touchAction: 'pan-y'
        }}
        role="dialog"
        aria-modal="true"
      >
        {/* iOS Pull Handle Bar */}
        <div className="ios-sheet-handle-bar" title="Drag down to close" />

        {/* Header */}
        <div className="ios-modal-header">
          <div className="ios-modal-header-icon-box">
            <UserPlus size={22} color="var(--accent-primary, #3b82f6)" />
          </div>
          <div className="ios-modal-header-text">
            <h3 className="ios-modal-title">
              {is2FAStep ? 'Two-Factor Authentication' : 'Add Another Account'}
            </h3>
            <p className="ios-modal-subtitle">
              {is2FAStep
                ? `Enter the 6-digit code for ${pendingEmail}`
                : 'Sign in to add this account to your quick switch list.'}
            </p>
          </div>
          <button
            type="button"
            className="ios-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="ios-modal-alert text-danger animate-shake">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Step 1: Email & Master Password */}
        {!is2FAStep ? (
          <form onSubmit={handleSubmitCredentials} className="ios-modal-form">
            {/* Native iOS Inset Grouped Card */}
            <div className="ios-input-group as-card-grouped">
              {/* Email Row */}
              <div className="ios-input-row">
                <div className="ios-input-icon">
                  <Mail size={18} />
                </div>
                <div className="ios-input-content">
                  <label className="ios-field-label">Email Address</label>
                  <input
                    type="email"
                    className="ios-native-input"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div className="ios-input-divider" />

              {/* Master Password Row */}
              <div className="ios-input-row">
                <div className="ios-input-icon">
                  <Lock size={18} />
                </div>
                <div className="ios-input-content">
                  <label className="ios-field-label">Master Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="ios-native-input"
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>
                <button
                  type="button"
                  className="ios-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="ios-security-note">
              <Shield size={14} className="text-accent" />
              <span>
                Zero-knowledge key derivation: Your master password is never sent to the server.
              </span>
            </div>

            <div className="ios-modal-actions">
              <button
                type="button"
                className="ios-btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="ios-btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <RefreshCw size={16} className="spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Add & Switch Account</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </form>
        ) : (
          /* Step 2: 2FA Verification */
          <form onSubmit={handleVerify2FA} className="ios-modal-form">
            <div className="ios-2fa-badge-card">
              <Shield size={28} className="text-accent" />
              <h4>Authenticator Code Required</h4>
              <p>
                Open your authenticator app (e.g. Google Authenticator, Authy) and enter the current
                6-digit code.
              </p>
            </div>

            <div className="ios-input-group" style={{ textAlign: 'center' }}>
              <label className="ios-input-label" style={{ justifyContent: 'center' }}>
                6-Digit Security Code
              </label>
              <input
                ref={otpInputRef}
                type="text"
                className="ios-2fa-code-input"
                placeholder="000000"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                required
              />
            </div>

            <div className="ios-modal-actions">
              <button
                type="button"
                className="ios-btn-secondary"
                onClick={() => setIs2FAStep(false)}
                disabled={loading}
              >
                Back
              </button>
              <button
                type="submit"
                className="ios-btn-primary"
                disabled={loading || otpCode.trim().length !== 6}
              >
                {loading ? (
                  <>
                    <RefreshCw size={16} className="spin" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={16} />
                    <span>Verify & Switch</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
}

/**
 * Switch Account 2FA Modal (iOS-Style)
 * Prompts for 2FA code when switching to an existing account that has 2FA enabled.
 */
export function SwitchAccount2FAModal({ isOpen, targetAccount, onClose, onVerified }) {
  const { switchAccount } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Touch drag state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartY = useRef(0);
  const currentDragY = useRef(0);

  useEffect(() => {
    if (isOpen) {
      setCode('');
      setError('');
      setDragY(0);
      setIsDragging(false);
      currentDragY.current = 0;
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  if (!isOpen || !targetAccount) return null;

  const handleTouchStart = (e) => {
    const touch = e.touches ? e.touches[0] : e;
    const isHandle = e.target.closest('.ios-sheet-handle-bar') || e.target.closest('.ios-modal-header');
    if (isHandle) {
      touchStartY.current = touch.clientY;
      setIsDragging(true);
      currentDragY.current = 0;
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches ? e.touches[0] : e;
    const deltaY = touch.clientY - touchStartY.current;
    if (deltaY > 0) {
      currentDragY.current = deltaY;
      setDragY(deltaY);
    } else {
      currentDragY.current = 0;
      setDragY(0);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (currentDragY.current > 75) {
      onClose();
    }
    setDragY(0);
    currentDragY.current = 0;
  };

  const handleVerifySwitch = async (e) => {
    e.preventDefault();
    setError('');

    const cleanCode = code.trim();
    if (cleanCode.length !== 6) {
      setError('Please enter the full 6-digit authentication code.');
      return;
    }

    setLoading(true);
    try {
      const res = await switchAccount(targetAccount.userId, cleanCode);
      setLoading(false);
      if (onVerified) onVerified(res.targetAccount);
      onClose();
    } catch (err) {
      setLoading(false);
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Invalid 2FA verification code. Please check your app and try again.';
      setError(msg);
    }
  };

  const modalContent = (
    <div className="ios-modal-backdrop animate-fade-in" onClick={onClose} role="presentation">
      <div
        className="ios-modal-container ios-switch-2fa-modal animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: isDragging ? 'none' : 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          touchAction: 'pan-y'
        }}
        role="dialog"
        aria-modal="true"
      >
        <div className="ios-sheet-handle-bar" title="Drag down to close" />

        <div className="ios-modal-header">
          <div className="ios-modal-header-icon-box" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
            <Shield size={22} />
          </div>
          <div className="ios-modal-header-text">
            <h3 className="ios-modal-title">Security Check</h3>
            <p className="ios-modal-subtitle">Two-Factor Authentication Required</p>
          </div>
          <button
            type="button"
            className="ios-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Target account banner */}
        <div className="ios-switch-target-card">
          <img
            src={
              targetAccount.avatar ||
              `https://ui-avatars.com/api/?name=${targetAccount.email}&background=random&color=fff`
            }
            alt={targetAccount.name}
            className="ios-switch-target-avatar"
          />
          <div className="ios-switch-target-details">
            <strong className="ios-switch-target-name">{targetAccount.name || targetAccount.email.split('@')[0]}</strong>
            <span className="ios-switch-target-email">{targetAccount.email}</span>
            <span className="ios-switch-target-role">{targetAccount.role || 'USER'}</span>
          </div>
        </div>

        {error && (
          <div className="ios-modal-alert text-danger animate-shake">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleVerifySwitch} className="ios-modal-form">
          <div className="ios-input-group" style={{ textAlign: 'center' }}>
            <label className="ios-input-label" style={{ justifyContent: 'center' }}>
              Enter 6-Digit Authenticator Code
            </label>
            <input
              ref={inputRef}
              type="text"
              className="ios-2fa-code-input"
              placeholder="000000"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
              autoFocus
            />
          </div>

          <div className="ios-modal-actions">
            <button
              type="button"
              className="ios-btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ios-btn-primary"
              disabled={loading || code.trim().length !== 6}
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} />
                  <span>Switch Account</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
}

/**
 * ReauthAccountModal
 * Prompts user for master password when switching to an account whose session has expired,
 * seamlessly renewing its zero-knowledge vault key, access token, and refresh token.
 */
export function ReauthAccountModal({ isOpen, targetAccount, onClose, onSuccess }) {
  const { addAccount, verify2FAAddAccount } = useAuth();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 2FA step state
  const [is2FAStep, setIs2FAStep] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [otpCode, setOtpCode] = useState('');

  // Touch drag state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartY = useRef(0);
  const currentDragY = useRef(0);

  const passwordInputRef = useRef(null);
  const otpInputRef = useRef(null);

  // Lock body scroll on mobile
  useEffect(() => {
    if (isOpen) document.body.classList.add('mobile-modal-open');
    else document.body.classList.remove('mobile-modal-open');
    return () => document.body.classList.remove('mobile-modal-open');
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setShowPassword(false);
      setError('');
      setIs2FAStep(false);
      setTempToken('');
      setOtpCode('');
      setDragY(0);
      setIsDragging(false);
      currentDragY.current = 0;
      setTimeout(() => passwordInputRef.current?.focus(), 120);
    }
  }, [isOpen]);

  useEffect(() => {
    if (is2FAStep && otpInputRef.current) {
      setTimeout(() => otpInputRef.current?.focus(), 100);
    }
  }, [is2FAStep]);

  if (!isOpen || !targetAccount) return null;

  const targetEmail = targetAccount.email || '';
  const targetName = targetAccount.name || targetEmail.split('@')[0] || 'User';

  const handleTouchStart = (e) => {
    const touch = e.touches ? e.touches[0] : e;
    const isHandle = e.target.closest('.ios-sheet-handle-bar') || e.target.closest('.ios-modal-header');
    if (isHandle) {
      touchStartY.current = touch.clientY;
      setIsDragging(true);
      currentDragY.current = 0;
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches ? e.touches[0] : e;
    const deltaY = touch.clientY - touchStartY.current;
    if (deltaY > 0) {
      currentDragY.current = deltaY;
      setDragY(deltaY);
    } else {
      currentDragY.current = 0;
      setDragY(0);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (currentDragY.current > 75) {
      onClose();
    }
    setDragY(0);
    currentDragY.current = 0;
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Please enter your master password.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await addAccount(targetEmail, password);
      setLoading(false);

      if (res?.requires2FA) {
        setIs2FAStep(true);
        setTempToken(res.tempToken);
        return;
      }

      if (onSuccess) onSuccess(targetAccount);
      onClose();
    } catch (err) {
      setLoading(false);
      const msg = err.response?.data?.message || err.message || 'Invalid credentials. Please verify and try again.';
      setError(msg);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    const cleanCode = otpCode.trim();
    if (cleanCode.length !== 6) {
      setError('Please enter the full 6-digit code.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await verify2FAAddAccount(tempToken, cleanCode, targetEmail, password);
      setLoading(false);
      if (onSuccess) onSuccess(targetAccount);
      onClose();
    } catch (err) {
      setLoading(false);
      const msg = err.response?.data?.message || err.message || 'Invalid 2FA code. Please try again.';
      setError(msg);
    }
  };

  const modalContent = (
    <div className="ios-modal-backdrop animate-fade-in" onClick={onClose} role="presentation">
      <div
        className="ios-modal-container ios-reauth-modal animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: isDragging ? 'none' : 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          touchAction: 'pan-y',
        }}
      >
        <div className="ios-sheet-handle-bar" />

        {/* Modal Header */}
        <div className="ios-modal-header">
          <div className="ios-modal-header-text">
            <h3 className="ios-modal-title">
              {is2FAStep ? 'Two-Factor Authentication' : 'Unlock & Switch Account'}
            </h3>
            <p className="ios-modal-subtitle">
              {is2FAStep
                ? `Enter the 6-digit code for ${targetEmail}`
                : `Session expired for ${targetEmail}. Please enter master password to switch.`}
            </p>
          </div>
          <button type="button" className="ios-modal-close-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Target Account Badge */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            margin: '0 0 16px 0',
            borderRadius: 12,
            background: 'rgba(56, 189, 248, 0.08)',
            border: '1px solid rgba(56, 189, 248, 0.2)',
          }}
        >
          <img
            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(targetName)}&background=0284c7&color=ffffff&bold=true`}
            alt={targetEmail}
            style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
          />
          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontWeight: 600, fontSize: '0.92rem', color: 'var(--text-primary, #fff)' }}>{targetName}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #94a3b8)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
              {targetEmail}
            </div>
          </div>
        </div>

        {error && (
          <div className="ios-modal-alert text-danger animate-shake">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {!is2FAStep ? (
          <form onSubmit={handleSubmitPassword} className="ios-modal-form">
            <div className="ios-input-group as-card-grouped">
              <div className="ios-input-row">
                <div className="ios-input-icon">
                  <Lock size={18} />
                </div>
                <div className="ios-input-content">
                  <label className="ios-field-label">Master Password</label>
                  <input
                    ref={passwordInputRef}
                    type={showPassword ? 'text' : 'password'}
                    className="ios-native-input"
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>
                <button
                  type="button"
                  className="ios-input-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="ios-modal-actions">
              <button type="button" className="ios-btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="ios-btn-primary" disabled={loading || !password}>
                {loading ? (
                  <>
                    <RefreshCw size={16} className="spin" />
                    <span>Unlocking...</span>
                  </>
                ) : (
                  <>
                    <KeyRound size={16} />
                    <span>Unlock & Switch</span>
                  </>
                )}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerify2FA} className="ios-modal-form">
            <div className="ios-otp-container">
              <input
                ref={otpInputRef}
                type="text"
                className="ios-otp-input"
                placeholder="000000"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
              />
            </div>

            <div className="ios-modal-actions">
              <button type="button" className="ios-btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="ios-btn-primary" disabled={loading || otpCode.trim().length !== 6}>
                {loading ? (
                  <>
                    <RefreshCw size={16} className="spin" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={16} />
                    <span>Confirm & Switch</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
}

